// Pre-shipment desk actions — ONE implementation for the desktop desk and the
// phone screens. Moved here from OrderList / OrderTable / BulkActionBar without
// changing what they write, so a phone and a desk can never record a COD call,
// a payment or a courier differently.
import { getDatabase, ref, update, push, set, get } from 'firebase/database';
import { canApproveDiscount, discountApprovalPending } from '../logic/discountPolicy';
import { auth } from '../../../integrations/firebase';
import { authHeaders } from '../../../core/api/authHeaders';
import { logInternalEvent, INTERNAL_ACTIONS } from '../../tracking/utils/internalTimeline';
import { useTodoStore } from '../../todos/store/todoStore';
import { restoreOrderStock } from '../../products/services/productService';
import { CustomerSyncService } from '../../../core/customer/CustomerSyncService';
import { CallActivityService } from '../../../core/customer/CallActivityService';
import { PaymentReconciliationService } from './PaymentReconciliationService';
import { sendOrderConfirmationSmsBatch } from './orderConfirmSms';
import { courierLabel } from '../logic/courierAssignPlan';
import { dispatchReadiness } from '../logic/dispatchReadiness';

const keyOf = (o) => o.firebaseKey || o.id;

/** The name every desk action records as "who did this". */
export function agentNameOf(currentUser, fallback = 'Unknown Agent') {
    const authUser = auth.currentUser;
    return currentUser?.name || currentUser?.displayName || currentUser?.email
        || authUser?.displayName || authUser?.email || fallback;
}

/**
 * Record the outcome of a call about an order (COD confirmation, call later,
 * hold, cancel…). `data` is what CallActionModal submits.
 */
export async function recordCallOutcome(order, data, currentUser) {
    const db = getDatabase();
    const key = keyOf(order);
    const timestamp = new Date().toISOString();
    const authUser = auth.currentUser;
    const agentName = agentNameOf(currentUser);
    const updates = {};

    // 1. Remark history
    if (data.remark) {
        const newRemark = push(ref(db, `orders/${key}/remarks`));
        await set(newRemark, { text: data.remark, createdAt: timestamp, type: data.type, agent: agentName });
    }

    // 2. COD confirmation
    const codStatuses = {
        CONFIRMED: 'confirmed', NO_RESPONSE: 'no_response', CALL_LATER: 'call_later',
        REJECTED: 'rejected', WRONG_NUMBER: 'no_response',
    };
    if (codStatuses[data.type]) {
        const codStatus = data.markCODConfirmed ? 'confirmed' : codStatuses[data.type];
        updates[`orders/${key}/codConfirmation/status`] = codStatus;
        updates[`orders/${key}/codConfirmation/lastCallAt`] = timestamp;
        updates[`orders/${key}/codConfirmation/confirmedBy`] = agentName;
        if (codStatus === 'confirmed') updates[`orders/${key}/codConfirmation/confirmedAt`] = timestamp;
        if (data.remark) updates[`orders/${key}/codConfirmation/note`] = data.remark;
        const actionMap = {
            confirmed: INTERNAL_ACTIONS.COD_CONFIRMED, no_response: INTERNAL_ACTIONS.COD_NO_RESPONSE,
            call_later: INTERNAL_ACTIONS.COD_CALL_LATER, rejected: INTERNAL_ACTIONS.COD_REJECTED,
        };
        await logInternalEvent(key, { action: actionMap[codStatus] || 'COD Status Updated', agent: agentName, details: data.remark || `Status: ${codStatus}` });
    }

    // 3. Follow-up (Call Later) — to the agent, or to the colleague picked in the form
    // (data.assignTo = their uid, data.assignToName = their name).
    const myUid = authUser?.uid || currentUser?.uid || currentUser?.id || '';
    const toOther = !!data.assignTo && data.assignTo !== 'self' && data.assignTo !== myUid;
    const assigneeUid = toOther ? data.assignTo : myUid;
    const assigneeName = toOther ? (data.assignToName || data.assignTo) : agentName;
    if (data.type === 'CALL_LATER' && data.followUpDate) {
        const followUpAt = data.followUpTime ? `${data.followUpDate}T${data.followUpTime}:00` : `${data.followUpDate}T10:00:00`;
        updates[`orders/${key}/followUp/required`] = true;
        updates[`orders/${key}/followUp/followUpAt`] = followUpAt;
        updates[`orders/${key}/followUp/assignedTo`] = toOther ? assigneeUid : 'self';
        updates[`orders/${key}/followUp/assignedToName`] = assigneeName;
        updates[`orders/${key}/followUp/reason`] = data.remark || 'Call later';
        updates[`orders/${key}/followUp/status`] = 'pending';
        updates[`orders/${key}/followUp/createdBy`] = agentName;
        updates[`orders/${key}/followUp/createdAt`] = timestamp;
        updates[`orders/${key}/codConfirmation/followUpAt`] = followUpAt;
        if (data.createTodo) {
            try {
                // Same shape the To-Do board writes (assignedTo = uid, participants), sent
                // through the proxy — a client RTDB write can silently never persist.
                const due = new Date(followUpAt).getTime();
                await useTodoStore.getState().createTask({
                    title: `Follow up COD order ${order.id}`,
                    description: data.remark || 'Customer requested callback',
                    orderId: order.id, customerName: order.customer?.name || '', phone: order.customer?.mobile || '',
                    dueAt: followUpAt, dueDate: Number.isFinite(due) ? due : null,
                    assignedTo: assigneeUid || assigneeName, assignedToName: assigneeName,
                    assignedBy: myUid, assignedByName: agentName, createdBy: myUid, createdByName: agentName,
                    participants: Object.fromEntries([assigneeUid, myUid].filter(Boolean).map(u => [u, true])),
                    taskScope: 'team', taskType: 'cod_followup',
                    type: 'cod_followup', status: 'pending', priority: 'high',
                }, { id: myUid, name: agentName });
                await logInternalEvent(key, { action: INTERNAL_ACTIONS.FOLLOWUP_CREATED, agent: agentName, details: `Follow-up scheduled for ${followUpAt}${toOther ? ` — assigned to ${assigneeName}` : ''}` });
            } catch (todoErr) { console.error('Failed to create follow-up task:', todoErr); }
        }
    }

    // 4. Status changes
    if (data.type === 'CNC') {
        updates[`orders/${key}/status`] = 'CNC';
        await restoreOrderStock(order);
    } else if (data.type === 'HOLD') {
        updates[`orders/${key}/status`] = 'Hold';
        updates[`orders/${key}/holdUntil`] = data.holdDate;
        await restoreOrderStock(order);
        await logInternalEvent(key, { action: INTERNAL_ACTIONS.HOLD_SET, agent: agentName, details: `Hold until ${data.holdDate}` });
    } else if (data.type === 'CANCEL') {
        updates[`orders/${key}/status`] = 'Cancelled';
        await restoreOrderStock(order);
        await logInternalEvent(key, { action: INTERNAL_ACTIONS.CANCELLED, agent: agentName, details: data.remark });
    } else if (data.type === 'REJECTED') {
        updates[`orders/${key}/status`] = 'Hold';
        await logInternalEvent(key, { action: INTERNAL_ACTIONS.COD_REJECTED, agent: agentName, details: data.remark });
    }

    // 5. Last agent
    updates[`orders/${key}/lastAgent`] = agentName;
    updates[`orders/${key}/lastActionAt`] = timestamp;
    if (Object.keys(updates).length > 0) await update(ref(db), updates);

    if (data.type === 'CANCEL') {
        try {
            const mob = order?.customer?.mobile;
            if (mob) await CustomerSyncService.applyOrderStatusEvent(mob, 'cancelled', { orderId: order.id || key, source: 'orderlist', user: { uid: agentName } });
        } catch (e) { console.warn('[CustomerSync] cancel sync failed:', e.message); }
    }

    // Surface the call in the Telecaller Workspace.
    try {
        const mob = order?.customer?.mobile;
        if (mob) {
            const RESULT_MAP = {
                CONFIRMED: 'Connected', NO_RESPONSE: 'Not Answered', CALL_LATER: 'Call Later',
                REJECTED: 'Not Interested', WRONG_NUMBER: 'Wrong Number', CNC: 'Not Answered',
                HOLD: 'Connected', CANCEL: 'Not Interested',
            };
            const followUpMs = (data.type === 'CALL_LATER' && data.followUpDate)
                ? new Date(`${data.followUpDate}T${data.followUpTime || '10:00'}:00`).getTime() : 0;
            await CallActivityService.logCall({
                mobile: mob, customerName: order.customer?.name, orderId: order.id || key,
                sourceModule: 'all_orders', callType: 'COD Confirmation',
                result: RESULT_MAP[data.type] || 'Connected', remark: data.remark,
                calledBy: agentName, calledByUid: authUser?.uid || '', calledByRole: currentUser?.role || '',
                followUpAt: followUpMs || undefined, assignedTo: assigneeName,
                assignToUid: followUpMs && toOther ? assigneeUid : undefined,
            });
        }
    } catch (e) { console.warn('[CallActivity] log failed:', e.message); }
}

/**
 * Mark a prepaid order paid by hand, with a reference — for money confirmed
 * outside Cashfree (bank transfer, UPI screenshot). Auditable: who, when, ref.
 */
export async function markPaidManually(order, note, currentUser) {
    const reference = String(note || '').trim();
    if (!reference) throw new Error('A reference or note is required to verify manually.');
    const db = getDatabase();
    const key = keyOf(order);
    const agentName = agentNameOf(currentUser, 'Admin');
    // The proxy writes the PAID verdict (verificationStatus PAID, mode Prepaid,
    // sourceOfTruth manual) with the verified login — the database refuses it from a
    // browser since the 1 Nov 2026 security review.
    const r = await fetch(`${PROXY_BASE}/api/orders/${encodeURIComponent(key)}/payment/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ reference }),
    });
    if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `Could not mark it paid (HTTP ${r.status}).`);
    }
    // Confirmed money moves the order out of its pre-payment status.
    const curStatus = String(order.status || '').toLowerCase();
    if (['pending', 'payment pending', 'payment failed'].includes(curStatus)) {
        await update(ref(db, `orders/${key}`), { status: 'Processed', updatedAt: Date.now() });
    }
    await logInternalEvent(key, { action: 'Payment verified manually', agent: agentName, details: `₹${order.payment?.total || 0} · ref ${reference}` });
    await closePaymentLink(order, `Paid manually · ref ${reference}`);
}

const PROXY_BASE = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

/**
 * Close the order's open payment link because the money was recorded another way, so
 * the customer cannot pay it twice (DP07276: advance recorded by hand, link still live).
 * The server asks Cashfree first — a link that WAS paid is recorded, not cancelled.
 * Best-effort: never blocks the manual record itself. Returns the server's answer.
 */
export async function closePaymentLink(order, reason) {
    const st = String(order?.payment?.paymentLink?.status || '').toUpperCase();
    if (!order?.payment?.paymentLink?.url || ['PAID', 'CANCELLED', 'EXPIRED'].includes(st)) return { closed: false };
    try {
        const r = await fetch(`${PROXY_BASE}/api/payment-links/close`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
            body: JSON.stringify({ key: keyOf(order), reason }),
        });
        return await r.json().catch(() => ({}));
    } catch (e) {
        console.warn('[closePaymentLink]', e.message);
        return { closed: false, error: e.message };
    }
}

/**
 * A manager approves a discount above the limit, which lets the order go to a
 * courier. Only manager / admin / super_admin may; the database rules refuse
 * anyone else, this check just says so politely first.
 */
export async function approveDiscount(order, currentUser) {
    const db = getDatabase();
    const key = keyOf(order);
    const uid = auth.currentUser?.uid;
    const role = uid ? String((await get(ref(db, `users/${uid}/role`))).val() || '') : '';
    if (!canApproveDiscount(role)) throw new Error('Only a manager can approve a discount above ₹100.');
    if (!discountApprovalPending(order)) throw new Error('This order has no discount waiting for approval.');
    const agentName = agentNameOf(currentUser, 'Manager');
    await update(ref(db, `orders/${key}/discountApproval`), { status: 'approved', approvedBy: agentName, approvedAt: Date.now() });
    await logInternalEvent(key, { action: 'Discount approved', agent: agentName, details: `₹${order.discountApproval?.amount || 0} off · requested by ${order.discountApproval?.requestedBy || '—'}` });
}

/**
 * Ask Cashfree about a prepaid order. Returns what Cashfree actually said —
 * never a guess — as { ok, status, paid, lines }. `ok:false` means Cashfree
 * could not be reached.
 */
export async function verifyWithCashfree(order) {
    const r = await PaymentReconciliationService.reconcileSingleOrder(order.id, true);
    if (!r.success) {
        return { ok: false, status: 'UNREACHABLE', paid: false, lines: [`Could not reach Cashfree for ${order.id}:`, r.error || r.reason || r.status || 'unknown error'] };
    }
    const lines = [`Order ${order.id}`, `Cashfree order id checked: ${r.cfId || '—'}`];
    if (!r.cfFound) {
        lines.push('', 'NOT FOUND on Cashfree (order_not_found).', '',
            'If you CAN see this order as Paid in the Cashfree dashboard, the ERP is querying a different Cashfree account/app than the website uses — compare the app id in the website admin (Settings → Cashfree) with the one configured on the proxy.',
            '', 'The order stays unverified. Use "Mark paid" with the Cashfree reference if you have confirmed it on the dashboard.');
    } else {
        lines.push(`Cashfree order status: ${r.cfOrderStatus || 'unknown'}`);
        if (r.cfAmount != null) lines.push(`Cashfree amount: ₹${r.cfAmount}`);
        if (r.payments?.length) {
            lines.push('', 'Payment attempts:');
            r.payments.forEach((pp) => lines.push(`  • ${pp.status || '?'} ₹${pp.amount ?? '?'}${pp.method ? ` (${pp.method})` : ''}${pp.id ? ` — ${pp.id}` : ''}`));
        } else {
            lines.push('', 'No payment attempt recorded.');
        }
        lines.push('');
        if (r.status === 'PAID') lines.push('✅ VERIFIED PAID — the order can now be dispatched.');
        else if (r.status === 'ACTIVE') lines.push('⏳ ACTIVE — the payment link is still open and the customer has not paid. Do not dispatch.');
        else if (r.status === 'FAILED') lines.push('❌ Payment FAILED at Cashfree.');
        else if (r.status === 'NEEDS_REVIEW') lines.push(`⚠️ Needs review: ${r.reason}`);
        else if (r.status === 'ABANDONED') lines.push('🚫 ABANDONED — order expired/terminated at Cashfree with no payment.');
        else lines.push(`Result: ${r.status}${r.reason ? ` — ${r.reason}` : ''}`);
    }
    return { ok: true, status: r.status, paid: r.status === 'PAID', lines };
}

/**
 * Write a courier (or 'hold') onto orders ALREADY cleared by
 * planCourierAssignment. Logs each, and texts each customer once (the server
 * refuses a second send per order).
 */
export async function assignCourier(orders, courier, currentUser) {
    if (!orders.length) return 0;
    // Belt and braces: every caller plans with planCourierAssignment first, but this
    // write is what puts an order in a courier desk — it must never take a not-ready one.
    if (courier && courier !== 'hold') {
        const notReady = orders.map(o => ({ o, r: dispatchReadiness(o) })).filter(x => !x.r.ok);
        if (notReady.length) {
            throw new Error(`NOT READY — cannot assign a courier:
${notReady.slice(0, 10).map(x => `• ${x.o.id}: ${x.r.label}`).join('\n')}`);
        }
    }
    const db = getDatabase();
    const updates = {};
    const timestamp = new Date().toISOString();
    const agentName = agentNameOf(currentUser, 'System');
    const label = courierLabel(courier);
    for (const order of orders) {
        const key = keyOf(order);
        updates[`orders/${key}/shipmentSorting/courier`] = courier;
        updates[`orders/${key}/shipmentSorting/assignedBy`] = agentName;
        updates[`orders/${key}/shipmentSorting/assignedAt`] = timestamp;
        updates[`orders/${key}/shipmentSorting/sortingStatus`] = 'assigned';
        updates[`orders/${key}/shipping/courierPreference`] = label;
    }
    await update(ref(db), updates);
    for (const order of orders) {
        await logInternalEvent(keyOf(order), { action: INTERNAL_ACTIONS.COURIER_ASSIGNED, agent: agentName, details: `Bulk assigned: ${label}` });
    }
    if (courier && courier !== 'hold') sendOrderConfirmationSmsBatch(orders.map(keyOf));
    return orders.length;
}

/**
 * Hand orders to their courier's desk (India Post / DTDC manager). Does NOT
 * mark them shipped — booking happens on the desk. `overrideReasons` maps an
 * order id to the checks an admin overrode, for the audit trail.
 */
export async function moveToCourierDesk(orders, currentUser, overrideReasons = null) {
    const db = getDatabase();
    const updates = {};
    const agentName = agentNameOf(currentUser, 'System');
    for (const order of orders) {
        const key = keyOf(order);
        const label = courierLabel(order.shipmentSorting?.courier);
        updates[`orders/${key}/shipmentSorting/sortingStatus`] = 'moved_to_courier_manager';
        updates[`orders/${key}/shipping/courier`] = label;
        updates[`orders/${key}/tracking/courier`] = label;
    }
    await update(ref(db), updates);
    for (const order of orders) {
        const key = keyOf(order);
        const label = courierLabel(order.shipmentSorting?.courier);
        const overridden = overrideReasons?.[order.id];
        await logInternalEvent(key, { action: INTERNAL_ACTIONS.MOVED_TO_COURIER_MANAGER, agent: agentName, details: `Moved to ${label} Manager${overrideReasons ? ' (Admin Override)' : ''}` });
        if (overridden?.length) {
            await logInternalEvent(key, { action: INTERNAL_ACTIONS.ADMIN_OVERRIDE, agent: agentName, details: `Override: ${overridden.join(', ')}` });
        }
    }
    return orders.length;
}
